/**
 * Declare your keepers — the manager-side replacement for the Keepers tab.
 *
 * The spreadsheet makes you do the hard part yourself: tick boxes, then work out by hand what
 * each player costs, whether two of them collide, which pick you no longer own, how many slots
 * a franchise tag eats and what the tax comes to. Every August that produces the same Discord
 * thread of people asking the commissioner to check their arithmetic.
 *
 * Three things this screen insists on:
 *
 *   The arithmetic is the app's job. Tick a player and the slate re-prices.
 *
 *   The CHOICES stay the manager's. When two keepers want the same round, the rules force a
 *   resolution but not a particular one — "you can effectively escalate whomever you want,
 *   based on their overlaps." So every keeper has a pick selector, and the app only fills in a
 *   default. Same for a pick you traded away: the app moves him, you can move him elsewhere.
 *
 *   Nothing computed is stored. The declaration records who you are keeping, the tag, and any
 *   pick you pinned by hand. Costs derive on read, so a rules correction after the deadline
 *   reprices every slate instead of freezing wrong numbers the way the sheet does.
 */

import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import rows from '../data/keepers-2026.json';
import { gmOfTeam, managerShort, teamOfGm } from '../domain/identity.js';
import { holdingsFor } from '../domain/picks2026.js';
import { useOwnership } from './useOwnership.js';
import { useRosterVersion } from './pairings.js';
import { dock } from './dock.js';
import { LEAGUE_2026 } from '../domain/config.js';
import { assignKeeperPicks, resolvePickIdentities } from '../domain/draftOrder.js';
import { LEAGUE_ZONE, clockIn, gateOf, inZone } from '../domain/deadlines.js';
import { countdownTo } from '../domain/countdown.js';
import { useNow } from './useNow.js';
import { faab, openingBalance } from '../domain/faab.js';
import { assessLuxuryTax } from '../domain/luxuryTax.js';
import {
  chargedFor, escalationOptions, keeperLocked, splitName, statusChips, tagState,
} from '../domain/keeperRow.js';
import { nflInfoOf } from './NflChip.js';
import { DeclarationRow } from './keeper/DeclarationRow.js';
import { PositionChip } from './chips.js';
import { liftAt, roundAtPoint, shouldLift } from './keeper/lift.js';
import { Escalation } from './keeper/Escalation.js';
import { CommitBar, SubmitSheet, type Line } from './keeper/CommitBar.js';
import {
  FORFEIT_REASONS, OverrideError, farmForfeitDraft, forfeitedGraduates, isForfeited,
  makeOverride, priorTagReason, priorlyFranchised,
  type ForfeitReason, type Override,
} from '../domain/overrides.js';
import { pickInRound, pickLabel, slotOf } from '../domain/picks.js';
import { andList, slateChange, slateSaid } from '../domain/slateDiff.js';
import { note } from './auditor.js';
import { EverySlate } from './EverySlate.js';
import { Linkify, Rule, useRuleIndex } from './RuleText.js';
import {
  atLeast, type DeclaredKeeper, type DraftState, type FaabEntry, type LeagueSettings,
  type LeagueStore, type Role,
} from '../store/types.js';

const SEASON = 2026;
const MGR = new Proxy({} as Record<string, string>, {
  get: (_t, team) => (typeof team === 'string' ? gmOfTeam(team) : undefined),
});
/** Each pick keeps the team whose slot it originally was — that is what numbers it. */
interface OwnedPick { round: number; from: string }
const TEAMS = 10;


interface Txn {
  date: string; label: string; time: string; type: string; method: string;
  gm: string; counterpartyGm: string;
  /** FAAB paid on a winning waiver claim; null on a free agent add, a drop or a trade. */
  bid: number | null;
}

interface Row {
  player: string; team: string; pos: string; def: boolean;
  val: number; cost: number | null; sleeper: boolean; cost2027: number | null;
  dropped: boolean; farm: boolean; mandatory: boolean;
  tagEligible: boolean; tagReason: string; tagCost: number | null;
  taggedSeason: number | null;
  /** How he was acquired: 'drafted' | 'udfa' | 'farm'. */
  acq: string;
  kept25: boolean;
  /** His 2025 draft slot, where he was drafted at all. */
  draftRound: number | null; draftPick: number | null;
  /** Farm provenance, present only for graduates. */
  farmYear?: number; farmBid?: number | null;
  /** The first 2025 drop, which is what costs the sleeper reward. */
  firstDrop: string | null; firstDropLabel: string | null;
  txns: Txn[];
}



/* TagChip removed: rule tags are chips on the row now, with callouts. */


/* tagWhy removed: the Can’t Tag callout prints the rule’s own reason. */


/* acquiredOf removed: provenance moved into the chips and their callouts. */


/**
 * The sortable columns, in two runs with the unsortable Acquired column between them.
 *
 * `2025 rd` is not in the design's seven, which reads Keep · Player · Pos · Acquired · Costs ·
 * Franchise · Rule tags. It stays because it is the input the cost is derived FROM: a manager
 * checking why Kyren Williams costs a 5th is reading the Grid from that round, and the band
 * colour on it is what says 3-9 is franchise territory. Acquired answers a different question —
 * where the player came from — and cannot stand in for it.
 */
/**
 * The rules this screen enforces. The chip in the header counts them, so the label cannot say six
 * while seven are being applied — the count is derived, like every other figure in the app.
 */
const DECLARE_RULES: ReadonlyArray<{ id: string; what: string }> = [
  { id: '2019-II-1', what: 'Who is eligible to be kept' },
  { id: '2023-keeper-expansion-eight', what: 'Eight slots, and the luxury tax above four' },
  { id: '2015-III-7', what: 'Which of your picks pays for him' },
  { id: '2025-P05', what: 'The franchise tag' },
  { id: '2017-II-2', what: 'The sleeper reward' },
  { id: '2013-III-1', what: 'Farm graduates and the mandatory year' },
  { id: '2019-II-5', what: 'A forfeited slot, and what it still costs' },
];



export function DeclareKeepers(
  { gm, actor, role, store }:
  { gm: string; actor: string; role: Role; store: LeagueStore },
) {
  const all = rows as unknown as Row[];
  /**
   * Which of the ten seats is his.
   *
   * `teamOfGm` rather than a scan of `MGR`'s keys: `MGR` is a Proxy with only a `get` trap, so
   * `Object.keys` on it returns the keys of its empty target — the scan found nothing for
   * everybody, and the screen rendered a manager with no roster and no error to explain it.
   *
   * Recomputed against the roster version, because a seat can change hands inside the app.
   */
  const rosterV = useRosterVersion();
  const myTeam = useMemo(() => teamOfGm(gm), [gm, rosterV]);
  /*
   * THE PICKS HE HOLDS TODAY — the ledger plus every trade settled since, not the build-time
   * file alone. A GM who sold his 4th on the Trades page was still being offered it here as a
   * place to charge a keeper. Holdings come back keyed by seat GM; this screen thinks in team
   * names, so they are translated once.
   */
  const { ownership } = useOwnership(store);
  const myPicks = useMemo<OwnedPick[]>(
    () => holdingsFor(gm, ownership, LEAGUE_2026.draftRounds)
      .map((h) => ({ round: h.round, from: teamOfGm(h.from) ?? myTeam })),
    [gm, ownership, myTeam],
  );
  const myRounds = useMemo(() => myPicks.map((p) => p.round), [myPicks]);

  const roster = useMemo(
    () => all.filter((r) => r.team === myTeam && r.cost !== null)
      .sort((a, b) => (a.cost ?? 99) - (b.cost ?? 99) || a.player.localeCompare(b.player)),
    [all, myTeam],
  );


  /*
   * WHICH MANDATORY GRADUATES HAVE BEEN RELEASED. Read from the override log rather than a field
   * on the slate, because releasing one departs from 2019-II-5 §4 — see `farmReleaseDraft`. A
   * departure that lives in the log is one the committee can find in August; a boolean on a
   * declaration is one nobody ever sees again.
   */
  const [overrides, setOverrides] = useState<Override[]>([]);
  useEffect(() => {
    let live = true;
    const load = () => store.overrides(SEASON).then((o) => { if (live) setOverrides(o); });
    void load();
    const off = store.subscribe(SEASON, (what) => { if (what === 'overrides') void load(); });
    return () => { live = false; off(); };
  }, [store]);
  const forfeited = useMemo(() => forfeitedGraduates(overrides), [overrides]);
  /*
   * TAGS THE APP DID NOT SHIP WITH. `data/franchise-tags.json` carries the five 2025 tags that
   * were recovered from Discord; a sixth found afterwards had nowhere to go but a redeploy. See
   * `priorlyFranchised` — keyed by player, because §3 is about the man and not about his owner.
   */
  const priorTags = useMemo(() => priorlyFranchised(overrides), [overrides]);
  const isGone = (player: string) => isForfeited(forfeited, gm, player);
  /**
   * MANDATORY UNTIL THE NFL SAYS OTHERWISE.
   *
   * A farm graduate's rookie year is not a choice (2013-III-1 §5), so his box is ticked and
   * locked. What unlocks it is the commissioner recording that he never made a 53-man roster:
   * 2015-III-1 ends the obligation to CARRY him in as many words — "though you no longer have to
   * carry the player" — and a manager may well want to keep him anyway, on a practice squad
   * stash he still believes in.
   *
   * WHAT DOES NOT UNLOCK IS THE COST. 2019-II-5 §1 takes the slot and charges the luxury tax as
   * though he had been kept, and neither depends on the tick. So after a forfeit the checkbox
   * answers "is he on my roster", not "does he cost me a slot" — see `charged` below, which is
   * what the tax is actually assessed on. Reading it the other way would hand a manager a free
   * keeper slot because the NFL cut his prospect, which is the exact opposite of a penalty.
   */
  const stillForced = (r: { player: string; mandatory: boolean }) => keeperLocked(r, isGone(r.player));
  const [forfeiting, setForfeiting] = useState<string | null>(null);
  const [forfeitWhy, setForfeitWhy] = useState('');
  const [forfeitKind, setForfeitKind] = useState<ForfeitReason>('cut');
  const [releaseErr, setReleaseErr] = useState<string | null>(null);

  /**
   * Release a graduate, or put him back.
   *
   * The write is an OVERRIDE, not a change to the slate, because it departs from 2019-II-5 §4 —
   * the rulebook forfeits the slot and the league has chosen to return it. Putting him back marks
   * the row reverted rather than deleting it, so "he was released for a fortnight in August" is
   * still answerable in November.
   */
  async function forfeitGraduate(player: string) {
    try {
      const o = makeOverride(
        farmForfeitDraft(gm, player, forfeitKind, forfeitWhy),
        actor, SEASON, new Date().toISOString(),
      );
      await store.saveOverride(o);
      setForfeiting(null);
      setForfeitWhy('');
      setReleaseErr(null);
    } catch (e) {
      setReleaseErr(e instanceof OverrideError ? e.message : 'That did not save.');
    }
  }

  async function undoForfeit(player: string) {
    const row = overrides.find(
      (o) => o.target === `farm-forfeit:${gm.toUpperCase()}:${player}` && !o.revertedAt,
    );
    if (!row) return;
    await store.saveOverride({
      ...row, revertedAt: new Date().toISOString(), revertedBy: actor.toUpperCase(),
    });
  }

  /**
   * Farm graduates are not optional — 2013-III-1 §5 makes the rookie year mandatory — UNLESS a
   * commissioner has released one who never made a 53-man roster. A released graduate drops out
   * of this list entirely, so he is not pre-ticked, not locked, and not counted against the cap.
   */
  /** Farm graduates are not optional — 2013-III-1 §5 — forfeited or not. */
  const mandatory = useMemo(
    () => roster.filter((r) => r.mandatory).map((r) => r.player),
    [roster],
  );

  const [picked, setPicked] = useState<Set<string>>(new Set(mandatory));
  const [tagged, setTagged] = useState<string | null>(null);
  /** Only the picks the manager has pinned by hand; everything else is left to the default. */
  const [pinned, setPinned] = useState<Record<string, number>>({});
  /**
   * The within-round choice, player → origin team, for a GM holding two picks in one round.
   * Separate from `pinned` because it answers a different question: `pinned` is WHICH ROUND
   * pays, this is WHICH OF THE TWO PICKS in that round.
   */
  const [pinnedFrom, setPinnedFrom] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<'draft' | 'submitted'>('draft');
  /**
   * THE SLATE AS IT STOOD BEFORE THIS EDIT, and the only copy of it anywhere.
   *
   * A declaration document holds its CURRENT state and nothing else, so the version before a save
   * is gone the instant the save lands — which is why `domain/auditLog.ts`, which derives events
   * from the records themselves, can say "submitted 6 keepers" and can never say WHICH SIX
   * changed. Tim, 31 August: "need some more detail in the logging ... he added Jonathan Brooks
   * to his keepers. Need that detail."
   *
   * So the diff is written as it happens, from the one place that still has both sides.
   */
  const lastSaved = useRef<Array<{
    player: string; franchiseTagged?: boolean; elevatedTo?: number | null;
  }>>([]);
  /** False until a record has been read, so a first slate is not reported as ten additions. */
  const everSaved = useRef(false);
  const [settings, setSettings] = useState<LeagueSettings | null>(null);
  const [draft, setDraft] = useState<DraftState | null>(null);
  const [ledger, setLedger] = useState<FaabEntry[]>([]);
  /** The rule that stopped the last attempt to tick a box, cleared on the next interaction. */
  const [refused, setRefused] = useState<{ player: string; why: string } | null>(null);
  /* Three disclosures, all shut on arrival: the rules behind the screen, the projected roster and
     the pick inventory. None of them is what a manager came here to do, and all three used to be
     open above the list he did come for — on a phone that is two screens of scrolling before the
     first name. */
  /*
    The `§` panel, opened on the whole SET this screen enforces rather than on one rule. It was a
    list in the rail for a day: a 320px column set seven titles at four words a line, which is a
    table of contents you have to decode. The panel is where a rule gets read anyway.
  */
  const openRules = useRuleIndex();
  /* Open on a desktop, where the rail has the room and the canvas draws the grid; shut on a
     phone, where it is a card between the summary and the list a manager came here for. */
  /** Which legal pick the pointer is over, so the grid can say "this one" and not "any of these". */
  const [over, setOver] = useState<number | null>(null);
  /*
    The lifted card's position, and the press behind it.

    The press is a ref rather than state because it changes on every pointermove and nothing
    renders off it — putting it in state would re-render the whole slate sixty times a second to
    move one absolutely-positioned box.
  */
  const [lift, setLift] = useState<{ left: number; top: number } | null>(null);
  const press = useRef<{
    player: string; from: number; fromY: number;
    grabX: number; grabY: number; w: number; lifted: boolean;
  } | null>(null);
  const [picksOpen, setPicksOpen] = useState(
    () => typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      && window.matchMedia('(min-width: 1100px)').matches,
  );
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  /** The keeper currently being moved, by drag or by click-then-place. */
  const [held, setHeld] = useState<string | null>(null);
  /** Clearing a submitted slate asks once. Destructive and there is no undo for it. */
  const [clearing, setClearing] = useState(false);
  /** Whose escalation strip is open, and where its caret sits on that row. */
  const [escalating, setEscalating] = useState<string | null>(null);
  const [caret, setCaret] = useState('50%');
  /** The submit sheet. Opening it is the only thing the commit bar's button does. */
  const [confirming, setConfirming] = useState(false);
  /**
   * Whether a SUBMITTED slate has been put back into edit. Submitting locks the screen; `Update
   * slate` unlocks it without withdrawing, so the league keeps seeing the last thing he sent
   * while he changes his mind about it.
   */
  const [editing, setEditing] = useState(false);
  /** What the commit bar says on the left. Autosave is a statement, not a control. */
  const [saveState, setSaveState] = useState<'saved' | 'saving' | 'offline'>('saved');
  /** When the slate was last sent, straight off the record rather than guessed from a save. */
  const [sentAt, setSentAt] = useState<string | null>(null);

  /**
   * Sent, and not currently being edited — the state the whole screen swaps for.
   *
   * Declared HERE, above `shown`, because `shown` closes over it to float a sent slate's keepers
   * to the top. Left where it reads more naturally, next to the other derived flags, it was a
   * temporal dead zone: the sort ran during the first render and the page threw before it drew.
   */
  const locking = status === 'submitted' && !editing;
  /*
    THE MOMENT, not the day. "Submitted Aug 20" leaves a manager who edited twice this afternoon
    unable to tell which version the league is looking at. In league time, like every other
    instant on this screen, so two managers comparing notes are reading the same clock.
  */
  const submittedAt = useMemo(() => {
    if (!sentAt) return null;
    const d = new Date(sentAt);
    if (Number.isNaN(d.getTime())) return null;
    const [day, clock] = inZone(sentAt, LEAGUE_ZONE)
      .replace(/^[A-Za-z]{3},\s*/, '')
      .split(/,\s*/);
    return clock ? `${day} at ${clock} ET` : `${day} ET`;
  }, [sentAt]);

  useEffect(() => {
    let live = true;
    void (async () => {
      const [d, s, ds, f] = await Promise.all([
        store.declarationFor(SEASON, gm), store.settings(SEASON), store.draftState(SEASON),
        store.faab(SEASON),
      ]);
      if (!live) return;
      setSettings(s);
      setDraft(ds);
      setLedger(f);
      setLoaded(true);
      if (!d) return;
      setPicked(new Set([...mandatory, ...d.keepers.map((k) => k.player)]));
      setTagged(d.keepers.find((k) => k.franchiseTagged)?.player ?? null);
      setPinned(Object.fromEntries(
        d.keepers.filter((k) => k.elevatedTo).map((k) => [k.player, k.elevatedTo!]),
      ));
      // Only the ORIGIN comes back from the record. The round stays derived, so a rules
      // correction after a save still reprices the slate — see the comment on `chargedPick`.
      setPinnedFrom(Object.fromEntries(
        d.keepers.filter((k) => k.chargedPick).map((k) => [k.player, k.chargedPick!.from]),
      ));
      setStatus(d.status);
      setSentAt(d.submittedAt ?? null);
      /* The version this edit started from. See `onRecord` in `save`. */
      lastSaved.current = d.keepers.map((k) => ({
        player: k.player,
        franchiseTagged: k.franchiseTagged,
        elevatedTo: k.elevatedTo ?? null,
      }));
      everSaved.current = true;
    })();
    return store.subscribe(SEASON, (what) => {
      if (what === 'settings') void store.settings(SEASON).then((s) => live && setSettings(s));
      if (what === 'draftState') void store.draftState(SEASON).then((s) => live && setDraft(s));
      if (what === 'faab') void store.faab(SEASON).then((f) => live && setLedger(f));
    });
  }, [store, gm, mandatory.join('|')]);

  /*
    A TICKING CLOCK, because every deadline here was computed from `Date.now()` at render time —
    correct exactly once. A page left open through the lock kept offering a Submit button the
    rules had already closed, and the countdown sat on whatever it said when the tab was opened.

    The cadence follows the countdown's own answer: once a minute for thirteen days, once a
    second for the last one, and never once it has passed. `countdownTo` returns the interval
    along with the figure, so the two cannot disagree about which regime we are in.
  */
  const [tick, setTick] = useState<number | null>(60_000);
  const now = useNow(tick);
  const left = countdownTo(settings?.keeperDeadline, now);
  useEffect(() => { setTick(left.tickMs); }, [left.tickMs]);

  const gate = gateOf(settings, 'keepers', now);
  /*
    The strip says two things and the design puts them at opposite ends: when it locks, and how
    long that leaves. League time only — the Mountain conversion the rest of the app appends is
    what pushed this onto a second line at 390px, and the full clock is still on the calendar.
  */
  const dueWhen = settings?.keeperDeadline
    ? `${inZone(settings.keeperDeadline, LEAGUE_ZONE)} ET`
    : null;
  /*
   * THE UNIT COMES WITH THE NUMBER. This printed `left.num` alone, so the strip read "Locks Mon,
   * Aug 31, 12:00 PM ET   10" and the 10 was 10 of nothing — days, hours and minutes all render
   * through here and the figure alone cannot say which. The rail below splits them on purpose
   * because it has two lines to put them on; this has one.
   */
  const dueLeft = left.passed || !left.num
    ? null
    : `${left.num}${left.unit ? ` ${left.unit}` : ''}`;
  /*
    The rail splits what the phone's strip runs together: the day on its own line, the clock under
    it, and the countdown as a figure in a box. `timeLeft` returns "13 days" or "4 hours" as one
    string, so it is split rather than reformatted — one implementation of "how long is left".
  */
  const lockDay = settings?.keeperDeadline
    ? inZone(settings.keeperDeadline, LEAGUE_ZONE).replace(/,\s*\d{1,2}:\d{2}\s*[AP]M$/, '')
    : null;
  const lockClock = settings?.keeperDeadline
    ? `${clockIn(settings.keeperDeadline, LEAGUE_ZONE)} ET`
    : null;
  const dueLeftNum = left.num;
  const dueLeftUnit = left.unit;
  /**
   * WHO MAY ASK FOR A WAY BACK IN, WHICH IS NOT THE SAME AS BEING GIVEN ONE.
   *
   * The deadline binds managers, not the people who have to fix things after it: a commissioner
   * has to be able to let one GM back in for a correction without moving the announced time for
   * everybody. That was true, and the implementation of it was wrong — `readOnly` simply excluded
   * a commissioner, so once the deadline passed the screen was as editable for him as it had been
   * the day before, with no act in between and nothing in the log.
   *
   * Tim, 3 September: "I should also not be able to do a normal unlock of keeper slate of mine or
   * anybody else's keepers. just a commish unlock exception that requires extra confirmation." The
   * point is his own slate as much as anybody's. A locked slate that the man who built the app can
   * quietly edit is not locked, and the tax has just been charged against it.
   *
   * So the lock now binds everybody, and a commissioner may lift it for one slate, once, by an act
   * that says what it is: `exception`.
   */
  const canOverride = atLeast(role, 'commissioner');

  /**
   * THE EXCEPTION IS SESSION STATE AND IS NOT WRITTEN ANYWHERE.
   *
   * Leaving the screen re-locks the slate, and that is the design rather than a shortcut. An
   * unlock that persists is a lock that is quietly off — nobody remembers to put it back, and the
   * next person to open the screen finds an open door with no idea who opened it or why. Making it
   * die with the page means the only state that outlives the correction is the correction itself
   * and the line in the log.
   */
  const [exception, setException] = useState(false);
  const [unlocking, setUnlocking] = useState(false);

  const readOnly = !gate.open && !exception;
  const chosen = useMemo(() => roster.filter((r) => picked.has(r.player)), [roster, picked]);
  /**
   * Who the slate is CHARGED for, which is not the same as who is on it.
   *
   * A graduate whose slot has been forfeited costs his manager that slot and its luxury tax
   * whether or not he keeps the player (2019-II-5 §1). Since the tick became unlockable, the two
   * lists diverge, and every number a manager decides on — slots used, tax, FAAB after tax —
   * comes off this one. The declaration is written from it too, so the charge survives a reload
   * and the rest of the league prices his slate the same way.
   */
  const charged = useMemo(
    () => chargedFor(roster, picked, isGone),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isGone is derived from these two
    [roster, picked, forfeited, gm],
  );

  /**
   * ONE ORDER, AND NO CONTROL FOR IT.
   *
   * Position, then what he costs, then his surname. A roster is read by position — a manager
   * looking at his running backs wants them together, and no sort by cost or by name puts them
   * there — and inside a position the decision is the price, which is why cost comes second.
   *
   * The dropdown that used to offer five orderings is gone. Four of them answered questions this
   * screen does not ask (nobody scans sixteen keepers alphabetically), it was the only control in
   * the summary bar and so had to be explained by being one, and on a phone it cost a 44px target
   * in the row of figures that matters most. A fixed order that is right beats five that are
   * mostly wrong.
   */
  const shown = useMemo(() => {
    const RANK: Readonly<Record<string, number>> = { QB: 0, RB: 1, WR: 2, TE: 3, DEF: 4 };
    const rank = (r: Row) => RANK[r.def ? 'DEF' : (r.pos ?? '')] ?? 9;
    return [...roster].sort((a, b) => {
      /*
       * A SENT SLATE PUTS ITSELF AT THE TOP. Once it is submitted the screen stops being a place
       * to decide and becomes a place to check, and what you check is the eight you sent — not
       * the eighteen you chose from. The rest of the roster stays below, browse-only.
       */
      if (locking) {
        const ak = picked.has(a.player) ? 0 : 1;
        const bk = picked.has(b.player) ? 0 : 1;
        if (ak !== bk) return ak - bk;
      }
      return rank(a) - rank(b)
        || (a.cost ?? 99) - (b.cost ?? 99)
        // Surname, not the whole string: "Kyren Williams" must sort under W, not under K.
        || splitName(a.player).surname.localeCompare(splitName(b.player).surname)
        || a.player.localeCompare(b.player);
    });
  }, [roster, picked, locking]);

  /** Escape drops whatever is being carried, which is what everyone tries first. */
  useEffect(() => {
    if (!held) return;
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setHeld(null); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [held]);

    /** A keeper's price before any collision or ownership adjustment. */
  const baseCost = (r: Row) => (r.player === tagged && r.tagCost !== null ? r.tagCost : r.cost!);

  const assessment = useMemo(() => {
    /*
      A forfeited graduate consumes no PICK. He is not on the board — 2015-III-1 ends the
      obligation to carry him and the cell goes back into the draft — so he is left out of the
      assignment even when his manager has chosen to keep him anyway.
    */
    const { assigned, errors: pickErrors } = assignKeeperPicks(
      chosen.filter((r) => !isGone(r.player)).map((r) => ({
        id: r.player,
        costRound: baseCost(r),
        preferred: pinned[r.player] ?? null,
      })),
      myRounds,
    );

    const tax = assessLuxuryTax({
      teamId: myTeam,
      /*
        CHARGED, NOT CHOSEN. A forfeited graduate counts against the slot allowance and the
        luxury tax whether or not his manager has kept him — 2019-II-5 §1 takes the spot and
        prices it as if he had been kept. The tick stopped meaning "does he cost me a slot" the
        moment it became possible to untick him, so the tax cannot be assessed on the tick.
      */
      declared: charged.map((r) => ({
        playerId: r.player,
        franchiseTagged: r.player === tagged,
        ...(isGone(r.player) ? { farmForfeited: true } : {}),
      })),
    }, LEAGUE_2026);

    const errors = [...pickErrors, ...tax.errors];
    if (tagged) {
      const t = roster.find((r) => r.player === tagged);
      /*
        AND THE SUBMIT CHECKS IT TOO, not only the toggle. A tag can be sitting on a slate from
        before a commissioner recorded the prior one — the row's control going quiet does not
        un-choose what is already chosen, and a bar that only hides a button is a bar a saved
        slate walks straight through.
      */
      if (priorTags.has(tagged)) {
        errors.push(`${tagged} cannot be franchised: ${priorTagReason(overrides, tagged)
          ?? 'he has been held under the franchise tag before (2025 P05 §3).'}`);
      } else if (t && !t.tagEligible) {
        errors.push(`${tagged} cannot be franchised: ${t.tagReason}`);
      }
    }
    for (const m of mandatory) {
      if (!picked.has(m)) {
        errors.push(`${m} is a farm graduate and must be kept for his rookie season (2013-III-1 §5).`);
      }
    }
    return { assigned, tax, errors };
  }, [chosen, tagged, pinned, myRounds, myTeam, roster, mandatory, picked, forfeited,
    priorTags, overrides]);

  /** Which of my picks a keeper already stands on, by round — the strip names the occupant. */
  const occupiedRounds = useMemo(() => {
    const m = new Map<number, string>();
    for (const [player, round] of assessment.assigned) m.set(round, player);
    return m;
  }, [assessment.assigned]);

  /*
    THE WARNINGS, IN ONE LINE EACH.
    `assessLuxuryTax` writes its notes for a rules context — the franchise one runs to four
    sentences and quotes the rule's own worked example. That is the right length in the rulebook
    and the wrong length above a table, where it pushed the board down the page and got read
    once. These say the consequence and hand the reasoning to the rule mark beside them.
  */
  const alerts = useMemo(() => {
    const out: React.ReactNode[] = [];
    if (tagged !== null) {
      out.push(
        /*
          THE BADGE LEADS THE SENTENCE (Tim, 8 September: "include that green T icon at the
          beginning of this sentence"). The line is the only place the three-slot price is
          stated, and a man reading it has just seen a green `T` on a card — naming the mark it
          is about is cheaper than making him hold "franchise tag" and "that green thing" as two
          separate ideas.
        */
        <><span className="kbadge t">T</span> Franchise tag uses three keeper slots. <Rule id="2025-P05" /></>,
      );
    }
    if (refused) out.push(<>{refused.why}</>);
    const lost = charged.filter((r) => isGone(r.player)).length;
    if (lost > 0) {
      out.push(
        <>
          {lost} forfeited farm keeper {lost === 1 ? 'slot' : 'slots'}.{' '}
          <Rule id="2019-II-5" />
        </>,
      );
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isGone is derived from these
  }, [tagged, charged, refused, forfeited, gm]);


  /**
   * WHICH pick within the round, for the rounds where that is a real question. Two picks in
   * one round is the only case with a choice in it; everywhere else this resolves to the one
   * pick the round holds.
   */
  const identities = useMemo(() => resolvePickIdentities(
    assessment.assigned,
    myPicks,
    {
      self: myTeam,
      preferredFrom: pinnedFrom,
      slotInRound: (from, round) => {
        const order = draft?.order ?? [];
        const slot = slotOf(order, MGR[from] ?? gm);
        return slot === null ? null : pickInRound(round, slot, TEAMS);
      },
    },
  ), [assessment.assigned, myPicks, myTeam, pinnedFrom, draft, gm]);

  /**
   * What the slate leaves him to bid with — the number a GM is actually deciding on, and the one
   * the screen never showed. The tax is charged in FAAB (2020-III-1 §4 killed the real-money
   * option), so an eighth keeper is not an abstract "₣88", it is ₣88 he will not have at the farm
   * auction on the same night as the draft.
   *
   * Opening balance, not the flat base: half of last season's leftovers carried (2018-II-4 §1),
   * so nobody starts level. Same derivation as League › FAAB, from the same two sources.
   */
  const faabNow = useMemo(() => {
    const start = openingBalance(gm);
    const moved = ledger
      .filter((e) => e.gm?.toUpperCase() === gm.toUpperCase())
      .reduce((n, e) => n + e.amount, 0);
    return start + moved;
  }, [gm, ledger]);
  const faabAfterTax = faabNow - assessment.tax.taxFaab;

  /**
   * The slot that numbers a pick is the ORIGINAL owner's, not mine — so a pick acquired in a
   * trade keeps the number it had on the other GM's line of the board.
   */
  const labelFor = (round: number) => {
    const order = draft?.order ?? [];
    const held = myPicks.find((p) => p.round === round);
    const owner = held ? MGR[held.from] ?? gm : gm;
    return pickLabel(round, slotOf(order, owner), TEAMS);
  };

  /**
   * The pick code alone, or null before the lottery has set the order.
   *
   * The null matters. `pickLabel` returns "9.——" when there is no order, and slates are declared
   * before the lottery runs — so the Costs column shows the round it consumes and simply has no
   * code to show yet, rather than printing a slot nobody has been assigned.
   */
  const codeFor = (round: number) => {
    const l = labelFor(round);
    return l.endsWith('——') ? null : l;
  };

  /**
   * `4.08` -> 38. The overall number the cost box prints under the code, worked back out of the
   * code rather than stored, so it cannot disagree with it. Null before the lottery, when there
   * is no code to work from.
   */
  const overallOfCode = (code: string): number | null => {
    const m = /^(\d+)\.(\d+)$/.exec(code.trim());
    if (!m) return null;
    return (Number(m[1]) - 1) * TEAMS + Number(m[2]);
  };

  /** The code of one SPECIFIC pick — needed where a GM holds two in the round. */
  const codeOfPick = (p: OwnedPick) => {
    const l = pickLabel(p.round, slotOf(draft?.order ?? [], MGR[p.from] ?? gm), TEAMS);
    return l.endsWith('——') ? null : l;
  };

  /** Every pick this GM holds in a round — a trade can leave him with two, numbered apart. */
  const labelsFor = (round: number) => {
    const order = draft?.order ?? [];
    return myPicks
      .filter((p) => p.round === round)
      .map((p) => pickLabel(round, slotOf(order, MGR[p.from] ?? gm), TEAMS));
  };

  /** What each round of the draft is doing for this GM, for the strip below the tiles. */
  const strip = useMemo(() => {
    const spent = new Map<number, string>();
    for (const [player, round] of assessment.assigned) spent.set(round, player);
    return Array.from({ length: LEAGUE_2026.draftRounds }, (_, i) => {
      const round = i + 1;
      const held = myRounds.filter((r) => r === round).length;
      return {
        round,
        held,
        keeper: spent.get(round) ?? null,
        // Held more than one and spent one: still have a live pick in this round.
        live: held - (spent.has(round) ? 1 : 0),
      };
    });
  }, [assessment.assigned, myRounds]);

  /**
   * What the slate is made of, by position — the chip row the canvas puts above the pick grid.
   *
   * Counted off `charged` rather than `picked`, for the same reason everything else is: a
   * forfeited graduate is still on the record and still costs a slot. Fixed order rather than
   * whatever the roster happens to be sorted by, so the row does not reshuffle as boxes are
   * ticked, and positions nobody is keeping are left out rather than shown as zero.
   */
  const posCounts = useMemo(() => {
    const ORDER = ['QB', 'RB', 'WR', 'TE', 'DEF'];
    const n = new Map<string, number>();
    for (const r of charged) {
      const pos = r.def ? 'DEF' : r.pos;
      if (!pos) continue;
      n.set(pos, (n.get(pos) ?? 0) + 1);
    }
    /*
      ALL FIVE, ALWAYS. Dropping the empty ones made the row reflow every time a box was ticked —
      chips sliding sideways under a grid that was not moving. A zero is also information on this
      screen: "no quarterback" is the thing a manager works out on paper anyway. It is drawn back
      rather than omitted, so the row is a fixed set of five and only the figures change.
    */
    return ORDER.map((p) => ({ pos: p, n: n.get(p) ?? 0 }));
  }, [charged]);

  /** How many of those picks a keeper is standing on — the disclosure's whole summary. */
  const spentOnKeepers = strip.filter((x) => x.keeper !== null).length;

  /**
   * Whether ticking this box is legal, and which rule says it is not.
   *
   * Validation moved here from save time deliberately. A manager who ticks a ninth box, reads a
   * red banner and then has to work out which of the nine to give back has been told he was
   * wrong after the fact; refusing at the click and naming the rule means the slate is never
   * briefly illegal and he never has to undo anything.
   */
  function refuseKeep(r: Row): string | null {
    if (picked.has(r.player)) return null; // taking one off is always allowed
    const next = assessment.tax.slotsUsed + 1;
    if (next > LEAGUE_2026.maxKeepers) {
      return `That would be ${next} slots of ${LEAGUE_2026.maxKeepers} — `
        + '2023-keeper-expansion-eight §1';
    }
    return null;
  }

  function refuseTag(r: Row): string | null {
    if (tagged === r.player) return null; // removing the tag is always allowed
    if (tagged) return `You have already franchised ${tagged} — one tag a season (2025 P05 §1).`;
    /*
      BEFORE the data's own answer, not after it. A commissioner records a prior tag precisely
      because the shipped data says `tagEligible: true` and is wrong; checking it second would
      let the stale answer win.
    */
    if (priorTags.has(r.player)) {
      return priorTagReason(overrides, r.player)
        ?? 'Already held under the franchise tag once (2025 P05 §3).';
    }
    if (!r.tagEligible) return r.tagReason;
    // The tag is three slots, so it costs two more than simply keeping him (2025 P05 §2).
    const next = assessment.tax.slotsUsed + (picked.has(r.player) ? 2 : 3);
    if (next > LEAGUE_2026.maxKeepers) {
      return `A tag is three slots, so that would be ${next} of ${LEAGUE_2026.maxKeepers} — `
        + '2025-proposal-05-franchise-tag §2';
    }
    return null;
  }

/*
 * On the design's `escalate to 2.08 ↑` link, which is NOT built here.
 *
 * It moves a keeper between two picks inside ONE round — 2.10 to 2.08 — which is only a choice
 * for a GM who holds two picks in that round after a trade. The app cannot express it:
 * `DeclaredKeeper.elevatedTo` is a round, so 2.10 and 2.08 are the same value to it. That is
 * QUESTIONS.md item 1, and it has to land before the link can mean anything.
 *
 * A round-level stand-in was tried and removed. Offering "escalate to the next round up" put a
 * link on every row, including "escalate to Rd 1" on a Round 2 keeper — rounds 1 and 2 are
 * always unspent, because nobody can keep a player who costs them — and a one-click invitation
 * to pay a first-round pick you had no reason to spend is a trap, not an affordance.
 *
 * Escalation still works, on the pick inventory above: drag a keeper onto any pick he can be
 * charged to, drop him on another to swap the two. That is reversible until the deadline, and it
 * is the control the design proposes replacing — so it stays until the replacement can be real.
 */

  const toggle = (r: Row) => {
    if (stillForced(r)) return;
    const why = refuseKeep(r);
    if (why) { setRefused({ player: r.player, why }); return; }
    setRefused(null);
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(r.player)) {
        next.delete(r.player);
        if (tagged === r.player) setTagged(null);
        setPinned(({ [r.player]: _drop, ...rest }) => rest);
        setPinnedFrom(({ [r.player]: _drop, ...rest }) => rest);
      } else next.add(r.player);
      return next;
    });
  };

  const toggleTag = (r: Row) => {
    const why = refuseTag(r);
    if (why) { setRefused({ player: r.player, why }); return; }
    setRefused(null);
    setTagged((prev) => (prev === r.player ? null : r.player));
    setPicked((prev) => new Set(prev).add(r.player));
    // His cost changes, so any pin he had may no longer be legal.
    setPinned(({ [r.player]: _drop, ...rest }) => rest);
    setPinnedFrom(({ [r.player]: _drop, ...rest }) => rest);
  };

  /** Hand a keeper back to the automatic assignment. Double-click his chip on the strip. */
  const clearPin = (player: string) => {
    setPinned(({ [player]: _drop, ...rest }) => rest);
    setPinnedFrom(({ [player]: _drop, ...rest }) => rest);
    setHeld(null);
  };

  /**
   * Can this keeper be paid from that round? He has to own a pick there, and it has to be at
   * or before his cost — moving a keeper later would be paying less than the Grid says.
   */
  function canPlace(player: string, round: number) {
    // A sent slate is a record, not a workbench. Withdraw or Update reopens it.
    if (readOnly || locking) return false;
    const r = roster.find((x) => x.player === player);
    if (!r) return false;
    if (round > baseCost(r)) return false;
    return myRounds.includes(round);
  }

  /**
   * Where the grid may OFFER to put him, which is not quite where the rules allow him.
   *
   * `canPlace` answers the rule — 2015-III-7, any pick at or before his cost round — and the pick
   * he is already standing on satisfies it. Offering that one is offering a no-op: a lit square
   * directly under the card in the manager's hand, and a release on it that changes nothing while
   * looking exactly like a release that does. So the rule is unchanged and the OFFER excludes it.
   *
   * One function rather than the same clause written at three call sites, because the three are
   * the outline, the fill and the drop, and a rule that disagrees between "you may put him here"
   * and "putting him here did something" is the worst shape this gesture can take.
   */
  function mayOffer(player: string, round: number) {
    if (!canPlace(player, round)) return false;
    return assessment.assigned.get(player) !== round;
  }

  /**
   * Drop him on a round. If someone else is already there, the two swap — that is the natural
   * reading of "escalate whomever you want", and it is what dragging one onto another looks
   * like it should do. If the swap would be illegal for the displaced keeper, his pin is
   * simply cleared and the assigner re-places him.
   */
  /**
   * ONE PIN PER ROUND, always. The single place a pin is written.
   *
   * A pick can only pay for one keeper, so two men pinned to the same round is not a slate the
   * assigner can resolve — it reports the second as "assigned a Round N pick that is not
   * available" and the board looks fine, because the man whose pin failed is simply missing from
   * the assignment and the others fill in around him. The error names a round nothing is visibly
   * standing on, which is the least useful thing it could say.
   *
   * It happened through STALE pins, not through the swap: `place` cleared the pin of whoever it
   * DISPLACED, read off the resolved assignment — and a keeper whose own pin had already failed
   * is not in that assignment, so he was never seen as displaced and his dead pin survived to
   * collide with the next one. Two writers made it twice as likely: the strip's tiles wrote pins
   * directly too, with no such clearing at all.
   *
   * So the invariant is enforced here rather than reasoned about at each call site: taking a
   * round takes it from whoever else was pinned to it, whether or not the assigner ever honoured
   * that pin.
   */
  const pinTo = (player: string, round: number, moveDisplacedTo?: number) => {
    setPinned((prev) => {
      const next: Record<string, number> = {};
      for (const [who, r] of Object.entries(prev)) {
        if (who === player || r === round) continue;
        next[who] = r;
      }
      next[player] = round;

      // The man who was standing there drops back to the pick this one just left, when he can
      // legally pay with it. Otherwise he goes back to the assigner.
      if (moveDisplacedTo !== undefined) {
        const displaced = [...assessment.assigned.entries()]
          .find(([other, r]) => r === round && other !== player)?.[0];
        const d = displaced ? roster.find((x) => x.player === displaced) : undefined;
        if (d && moveDisplacedTo <= baseCost(d)) next[d.player] = moveDisplacedTo;
      }
      return next;
    });
    setRefused(null);
  };

  function place(player: string, round: number) {
    pinTo(player, round, assessment.assigned.get(player));
    setHeld(null);
  }

  async function save(next: 'draft' | 'submitted') {
    setSaving(true);
    /* ONE INSTANT for the write and for the log line about it — see `mergeEvents`. */
    const sentIso = new Date().toISOString();
    // `charged`, not `chosen` — a forfeited graduate stays on the record even when his manager
    // has taken him off the roster, or his slot and his tax would vanish on the next reload.
    const keepers: DeclaredKeeper[] = charged.map((r) => ({
      player: r.player,
      ...(r.player === tagged ? { franchiseTagged: true } : {}),
      ...(pinned[r.player] ? { elevatedTo: pinned[r.player] } : {}),
      // The pick this save resolved to — the record QUESTIONS.md item 1 asked for, so keeper
      // history can say "9.04" instead of "Round 9 (pick not logged)".
      ...(identities.has(r.player) ? { chargedPick: identities.get(r.player)! } : {}),
      ...(isGone(r.player) ? { farmForfeited: true } : {}),
    }));
    await store.saveDeclaration({
      season: SEASON, gm, keepers, status: next,
      note: undefined,
      // Whoever actually clicked, which is not always the GM whose slate this is.
      updatedBy: actor,
      updatedAt: sentIso,
      ...(next === 'submitted' ? { submittedAt: sentIso } : {}),
    });
    setStatus(next);
    if (next === 'submitted') setSentAt(sentIso);

    /*
     * WHAT CHANGED, WRITTEN DOWN. After the save, so a refused write never leaves a log line
     * claiming something happened — the same rule as the lottery's `record` and the ranking's
     * `Resubmit`, and for the same reason.
     */
    const changed = slateChange(lastSaved.current, keepers);
    if (!changed.quiet || next === 'submitted') {
      note(store, actor, {
        at: sentIso,
        kind: 'keeper',
        what: slateSaid(changed, {
          submitted: next === 'submitted',
          total: keepers.length,
          first: !everSaved.current || lastSaved.current.length === 0,
        }),
        target: `declarations/${SEASON}-${gm}`,
        ...(gm.toUpperCase() !== actor.toUpperCase() ? { onBehalfOf: gm } : {}),
        detail: {
          ...(changed.added.length ? { Added: changed.added.join(' · ') } : {}),
          ...(changed.removed.length ? { Dropped: changed.removed.join(' · ') } : {}),
          ...(changed.tagged.length ? { 'Franchise tag on': changed.tagged.join(' · ') } : {}),
          ...(changed.untagged.length ? { 'Tag taken off': changed.untagged.join(' · ') } : {}),
          ...(changed.moved.length
            ? { Moved: changed.moved.map((m) => `${m.player} → round ${m.to}`).join(' · ') }
            : {}),
          'Slate now': keepers.map((k) => k.player).join(' · ') || 'nobody',
        },
      });
    }
    lastSaved.current = keepers.map((k) => ({
      player: k.player,
      franchiseTagged: k.franchiseTagged,
      elevatedTo: k.elevatedTo ?? null,
    }));
    everSaved.current = true;
    setSaving(false);
  }

  /**
   * Take the slate back.
   *
   * There was no way out of a submission: once sent, the only edit was another submission, so a
   * manager who submitted early and then traded the pick his keeper was charged to had a slate he
   * could change but not withdraw. This writes an empty declaration back as a DRAFT, which is the
   * same shape as never having sent one — `EverySlate` counts slates by status, so the league
   * stops seeing it immediately.
   *
   * The tick boxes go back to the mandatory farm graduates rather than to nothing. Those are not
   * a choice (2022-farm-system-clarification), so "clear" cannot mean clearing them.
   */
  async function clearSlate() {
    setSaving(true);
    const at = new Date().toISOString();
    await store.saveDeclaration({
      season: SEASON, gm, keepers: [], status: 'draft',
      note: undefined,
      updatedBy: actor,
      updatedAt: at,
    });
    setPicked(new Set(mandatory));
    setTagged(null);
    setPinned({});
    setPinnedFrom({});
    setStatus('draft');
    setClearing(false);

    /* Withdrawing is the loudest thing on this screen and had no line in the log at all. */
    const cleared = lastSaved.current.map((k) => k.player);
    note(store, actor, {
      at,
      kind: 'keeper',
      what: cleared.length
        ? `Took his slate back — dropped ${andList(cleared)}`
        : 'Took his slate back',
      target: `declarations/${SEASON}-${gm}`,
      ...(gm.toUpperCase() !== actor.toUpperCase() ? { onBehalfOf: gm } : {}),
      detail: {
        ...(cleared.length ? { Dropped: cleared.join(' · ') } : {}),
        'Slate now': 'nobody — back to a draft',
      },
    });
    lastSaved.current = [];
    setSaving(false);
  }

  const blocked = assessment.errors.length > 0;

  /*
    WOULD RELEASING THE PINS FIX IT?
    Asked before the button is offered, by running the assigner with every hand-placed pick
    dropped. If the answer is no the slate is over-full rather than badly packed, and the only
    remedy is taking somebody off — so the button says so instead of doing nothing when pressed.

    The assigner's unpinned pass is greedy from the most expensive keeper down, taking the LATEST
    pick at or before his cost, which is the cheapest legal arrangement there is. So "no errors
    with nothing pinned" really does mean "no legal arrangement was being blocked by the pins".
  */
  const lowestCostFixes = useMemo(() => {
    if (!blocked) return false;
    const { errors } = assignKeeperPicks(
      chosen.filter((r) => !isGone(r.player)).map((r) => ({
        id: r.player, costRound: baseCost(r), preferred: null,
      })),
      myRounds,
    );
    return errors.length === 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isGone/baseCost derive from these
  }, [blocked, chosen, myRounds, tagged, forfeited, gm]);

  /** Hand every keeper back to the assigner. The pins are what were in its way. */
  const releaseAllPins = () => {
    setPinned({});
    setPinnedFrom({});
    setHeld(null);
    setRefused(null);
  };

  /**
   * Back to the slate the rules give him before he does anything: the mandatory farm graduates,
   * and nothing else. Not an empty set — a graduate's rookie year is not a choice (2013-III-1 §5),
   * so "reset" cannot mean unticking him.
   */
  const resetSlate = () => {
    setPicked(new Set(mandatory));
    setTagged(null);
    setPinned({});
    setPinnedFrom({});
    setHeld(null);
    setRefused(null);
    setEscalating(null);
  };


  /** What the submit sheet lists. Read-only, so it is built from the same numbers the row shows. */
  const submitLines = useMemo<Line[]>(() => charged.map((r) => {
    const landed = assessment.assigned.get(r.player);
    const round = landed ?? baseCost(r);
    const id = identities.get(r.player);
    const code = (id ? codeOfPick(id) : codeFor(round)) ?? `Rd ${round}`;
    return {
      player: r.player,
      club: nflInfoOf(r.player)?.team ?? null,
      pos: r.pos,
      code,
      overall: overallOfCode(code),
      tagged: r.player === tagged,
    };
  }), [charged, assessment.assigned, identities, tagged, draft]);

  /*
   * AUTOSAVE. There is no save button, so the slate has to be safe without one — but a write per
   * keystroke of a checkbox is ten writes to cross a roster, so it settles first. 600ms is long
   * enough that ticking four men in a row is one write and short enough that nobody navigates
   * away inside it.
   *
   * It never fires before the screen has loaded (that would write an empty slate over a real one
   * on every open), never after the deadline, and never with a status of its own — it keeps
   * whatever the slate already is, so editing a submitted slate does not silently unpublish it.
   */
  useEffect(() => {
    if (!loaded || readOnly) return undefined;
    if (confirming) return undefined;
    setSaveState('saving');
    const t = window.setTimeout(() => {
      void save(status)
        .then(() => setSaveState('saved'))
        .catch(() => setSaveState('offline'));
    }, 600);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `save` closes over all of these
  }, [loaded, readOnly, picked, tagged, pinned, pinnedFrom, forfeited, status, confirming]);

  /*
    The lock card — the rail's version of the deadline strip, the summary bar and the commit bar
    at once, because on a desktop those three are one block of consequence rather than three bars
    competing for the top and bottom of a small screen.

    Built here rather than inline so the rail reads as three cards, and so the figures come off
    exactly the same `assessment` the bars use.
  */
  const lockCard = (
    <div className="kdlock">
      <div className="kdlocktop">
        <span>
          <span className="l">{gate.passed ? 'Keepers locked' : 'Keepers lock'}</span>
          <b className="day">{lockDay ?? '—'}</b>
          <span className="clock">{lockClock ?? gate.label}</span>
        </span>
        {dueLeft && (
          /* Amber is "hurry". Once the slate is in there is nothing to hurry for, so the same
             figure stays — the deadline has not moved — and stops shouting. */
          <span
            className={`kdcount${locking ? ' done' : ''}${left.live ? ' live' : ''}`}
            /* Announced only in the last day; a change every minute for a fortnight is noise. */
            aria-live={left.live ? 'off' : undefined}
          >
            <b>{dueLeftNum}</b>
            <span>{dueLeftUnit}</span>
          </span>
        )}
      </div>

      <div className="kdlockrow">
        <span>Slots used</span>
        <span>
          <b className={assessment.tax.slotsUsed > LEAGUE_2026.maxKeepers ? 'bad' : ''}>
            {assessment.tax.slotsUsed}
          </b>
          <span className="of"> of {LEAGUE_2026.maxKeepers}</span>
        </span>
      </div>
      <div className="kdlockrow">
        <span>Luxury tax</span>
        <b className={assessment.tax.taxFaab > 0 ? 'warn' : ''}>{faab(assessment.tax.taxFaab)}</b>
      </div>
      <div className="kdlockrow">
        <span>FAAB after tax</span>
        <b className={faabAfterTax < 0 ? 'bad' : ''}>{faab(faabAfterTax)}</b>
      </div>

      {!readOnly && (
        <div className={`kdlockfoot${locking ? ' sent' : ''}`}>
          {locking ? (
            <>
              <span className="kdsent">Submitted{submittedAt ? ` ${submittedAt}` : ''}</span>
              <span className="kdlockacts">
                <button type="button" className="kdsubmit" onClick={() => setEditing(true)}>
                  Update slate
                </button>
                <button type="button" className="kdwithdraw" onClick={() => setClearing(true)}>
                  Withdraw
                </button>
              </span>
            </>
          ) : (
            <>
              <span>
                {saveState === 'saving' ? 'Saving…'
                  : saveState === 'offline' ? 'Saved on this device' : 'Saved automatically'}
              </span>
              <button type="button" className="kdsubmit"
                disabled={saving || blocked || charged.length === 0}
                onClick={() => setConfirming(true)}>
                Submit slate
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="app">
      <Linkify>
       <div className="kdpage">
        {/*
          "Declare your keepers" on a desktop, where the canvas has the room for it and prints it;
          "Declare" on a phone, where the tab you pressed already says Keepers and the list under
          it is plainly a roster. Both strings are here and CSS chooses — a title that changes with
          the width has to, or the phone header spends two lines saying what the tab said.
        */}
        <header className="kdhead">
          <h1><span className="wide">Declare your keepers</span><span className="narrow">Declare</span></h1>
          <button type="button" className="kdruleschip"
            onClick={() => openRules({ title: 'Rules on this screen', rules: DECLARE_RULES })}>
            <span className="mark" aria-hidden="true">§</span>
            {DECLARE_RULES.length} rules
          </button>
        </header>

        {/*
          THE WHOLE STRIP SWAPS, not just the button. A slate that has been sent is not a
          deadline any more — the amber countdown telling a manager to hurry is answering a
          question he has already answered, so it becomes a green statement of when he sent it.
        */}
        {locking ? (
          <div className="kddue sent">
            <span>Submitted{submittedAt ? ` ${submittedAt}` : ''}</span>
          </div>
        ) : (
          <div className={`kddue${gate.urgent ? ' urgent' : ''}${readOnly ? ' closed' : ''}`}>
            <span className="when">
              {gate.passed ? 'Locked' : 'Locks'} {dueWhen ?? gate.label}
              {readOnly && ' — read-only'}
              {/*
                THE STRIP SAYS WHICH OF THE TWO SHUT STATES THIS IS. It used to read "closed for
                managers, open to you", which described a standing privilege; there is no such
                privilege now. Either it is shut, or somebody deliberately opened it and the strip
                names him rather than the role.
              */}
              {!gate.open && exception && ` — unlocked by ${managerShort(actor)}, exception`}
            </span>
            {dueLeft && <span className="left">{dueLeft}</span>}
            {!gate.open && canOverride && (exception ? (
              <button type="button" className="kdunlock on" onClick={() => setException(false)}>
                Re-lock
              </button>
            ) : (
              <button type="button" className="kdunlock" onClick={() => setUnlocking(true)}>
                Unlock
              </button>
            ))}
          </div>
        )}

        {/*
          Three figures and a sort, in the order the decision is actually made: how many slots this
          slate uses, what that costs in tax, and — the number the screen never showed — what it
          leaves him to bid with. The tax is charged in FAAB (2020-III-1 §4 removed the real-money
          option), so an eighth keeper is not an abstract ₣88; it is ₣88 he will not have at the
          farm auction, on the same night as the draft.

          The ladder, the free-slot count and the "from ₣140" it used to carry are gone. They
          explained the numbers to a manager who can see them change as he ticks rows, which is a
          better explanation than any sentence, and the § chip above holds the rule for anyone who
          wants the text.
        */}
        <section className="kdtally">
          <div className="kdstat">
            <span className="l">Slots</span>
            <span className="v">
              <b className={assessment.tax.slotsUsed > LEAGUE_2026.maxKeepers ? 'bad' : ''}>
                {assessment.tax.slotsUsed}
              </b>
              <span className="of">of {LEAGUE_2026.maxKeepers}</span>
            </span>
          </div>
          <div className="kdstat">
            <span className="l">Tax</span>
            <span className="v">
              <b className={assessment.tax.taxFaab > 0 ? 'warn' : ''}>{faab(assessment.tax.taxFaab)}</b>
            </span>
          </div>
          <div className="kdstat">
            <span className="l">FAAB left</span>
            <span className="v">
              <b className={faabAfterTax < 0 ? 'bad' : ''}>{faab(faabAfterTax)}</b>
            </span>
          </div>

        </section>

        {/*
          THE RAIL. On a desktop it is a sticky column beside the table carrying everything the
          phone puts in sticky bars — when the slate locks, what it costs, the submit, and the
          pick inventory. On a phone it is not a rail at all: it flows in the page above the list,
          in the order the design draws it. The MARKUP IS THE SAME either way, which is the point
          — a second copy of the tax in a second component is a second chance to be wrong about it.
        */}
        {/*
          THE CARD ITSELF, in the air. Not a drag image — a real element that follows the pointer,
          so it can be tilted and shadowed and can look picked up rather than photographed.
          `pointer-events: none`, or the thing under the pointer would always be this and
          `elementFromPoint` could never find the tile beneath it.
        */}
        {lift && held && (
          /*
            AND IT SAYS WHETHER RELEASING WOULD DO ANYTHING. `over` is the round under the pointer
            that can legally take this keeper; null means either empty space or a pick that cannot
            have him. Every other signal in this gesture is on the grid, under the hand — the card
            is the one surface guaranteed to be in view, so it carries the refusal.
          */
          <span className={`kdlift${over === null ? ' noplace' : ''}`}
            style={{ left: lift.left, top: lift.top, width: press.current?.w }}>
            <span className="rd">{labelFor(assessment.assigned.get(held) ?? 0)}</span>
            <b>{splitName(held).surname}</b>
            {over === null && <span className="kdliftno">not here</span>}
          </span>
        )}

        <aside className="kdrail">

        {lockCard}

        {/*
          THE WARNING, BETWEEN THE FIGURES AND THE PICKS. What used to be here was three
          sentences of rule text set above the table, wide enough to push the board down and long
          enough that nobody read it twice — so it is one line, in the rail, against the numbers
          it explains. Under the lock card rather than over it: the card is the thing being
          looked at, and a banner above it pushes the deadline down the column.
        */}
        {alerts.map((a, i) => (
          // eslint-disable-next-line react/no-array-index-key -- a fixed, ordered set of two
          <p className="kdalert" key={i}>{a}</p>
        ))}

        {/* The mini board, and the place you resolve conflicts. Drag a keeper onto any pick you
            own at or before his cost round; drop him on another keeper to swap the two. */}
        <section className="card kdfold">
          <button type="button" className="kdfoldhead" aria-expanded={picksOpen || held !== null}
            onClick={() => setPicksOpen((v) => !v)}>
            <h3 className="striphead">Your picks</h3>
            <span className="mut">
              {spentOnKeepers} of {strip.filter((x) => x.held > 0).length} spent
              <i className="kdcaret" aria-hidden="true" />
            </span>
          </button>
          {(picksOpen || held !== null) && (<>
          {/* Sized to the grid below it, so the two read as one block rather than a banner over
              a table. All five positions, always — see `posCounts`. */}
          <ul className="kdposrow">
            {posCounts.map(({ pos, n }) => (
              <li key={pos} className={n === 0 ? 'none' : ''}>
                {/* The one position chip the whole app draws — see `ui/chips.tsx`. */}
                <PositionChip pos={pos} />
                <b>{n}</b>
              </li>
            ))}
          </ul>
          {/* While carrying, the other chips stop swallowing clicks and drops so they land on
              the round underneath — dropping one keeper onto another is how you swap them. */}
          {/* `sent` turns the occupied picks green and takes the hand off them — the same
              green the rows and the countdown use, so one state has one colour. */}
          <ol className={`pickstrip${held ? ' carrying' : ''}${locking ? ' sent' : ''}`}>
            {strip.map((s) => {
              /*
                NOT THE PICK HE IS ALREADY ON. `canPlace` allows it — a keeper may always be
                charged to his own pick, and that rule is right — but OFFERING it is offering a
                no-op, and it put a lit square directly under the card in the manager's hand.
              */
              const legal = held !== null && mayOffer(held, s.round);
              return (
                <li
                  key={s.round}
                  className={[
                    s.held === 0 ? 'gone' : s.keeper ? 'used' : s.live > 1 ? 'extra' : 'open',
                    legal ? 'target' : '',
                    legal && over === s.round ? 'over' : '',
                    held && !legal && s.keeper !== held ? 'faded' : '',
                  ].filter(Boolean).join(' ')}
                  title={
                    s.held === 0 ? `Round ${s.round} — traded away, you hold no pick here`
                      : s.keeper ? `${labelsFor(s.round)[0]} — ${s.keeper}. Drag him to an earlier pick.`
                      : s.live > 1 ? `You hold ${s.live} picks here: ${labelsFor(s.round).join(' and ')}`
                      : `${labelsFor(s.round)[0]} — yours to use`
                  }
                  /*
                    ONE GESTURE, TWO DEVICES. See `keeper/lift.ts` for why this is not HTML5
                    drag-and-drop: that has no touch support, its drag image covers the squares
                    it is being dragged over, and it cannot say which target is under the pointer
                    without a handler on every one of them. Pointer events do all three, and the
                    phone stops needing a mode of its own.

                    `data-round` is how a point resolves to a pick — read off the element rather
                    than worked out from coordinates, because the grid's geometry is the
                    browser's business.
                  */
                  data-round={s.round}
                  onPointerDown={(e) => {
                    if (readOnly || locking || !s.keeper || e.button !== 0) return;
                    const tile = e.currentTarget.getBoundingClientRect();
                    press.current = {
                      player: s.keeper,
                      from: e.clientX,
                      fromY: e.clientY,
                      grabX: e.clientX - tile.left,
                      grabY: e.clientY - tile.top,
                      w: tile.width,
                      lifted: false,
                    };
                    // So the drag survives the pointer leaving this element, which it will.
                    e.currentTarget.setPointerCapture(e.pointerId);
                  }}
                  onPointerMove={(e) => {
                    const p0 = press.current;
                    if (!p0) return;
                    if (!p0.lifted) {
                      if (!shouldLift(e.clientX - p0.from, e.clientY - p0.fromY)) return;
                      p0.lifted = true;
                      setHeld(p0.player);
                    }
                    setLift(liftAt({ x: e.clientX, y: e.clientY },
                      { x: p0.grabX, y: p0.grabY }));
                    const round = roundAtPoint(document, e.clientX, e.clientY);
                    setOver(round !== null && mayOffer(p0.player, round) ? round : null);
                  }}
                  onPointerUp={(e) => {
                    const p0 = press.current;
                    press.current = null;
                    e.currentTarget.releasePointerCapture?.(e.pointerId);
                    setLift(null);
                    /*
                      No press of its own means this tile was never the source — it is the SECOND
                      TAP of a carry that began somewhere else, which is how an empty pick gets
                      chosen. Handled here rather than in an `onClick`, because a click fires
                      after this and the two fought: the click saw the carry this handler had just
                      started and immediately put the keeper back down, so tapping a card to pick
                      it up did nothing at all.
                    */
                    if (!p0) {
                      if (legal && held) place(held, s.round);
                      return;
                    }
                    if (!p0.lifted) {
                      /*
                        It never moved, so it was a tap — which is the OTHER way to do this and
                        still the better one on a phone with one hand full. Toggle the carry and
                        leave the grid waiting for a second tap.
                      */
                      if (legal && held) { place(held, s.round); return; }
                      setHeld((h) => (h === p0.player ? null : p0.player));
                      return;
                    }
                    const target = roundAtPoint(document, e.clientX, e.clientY);
                    /*
                      Dropping him back where he came from is not a move and not a mistake — it is
                      putting him down. Same ending as a drop on empty space: the carry ends and
                      nothing is written.
                    */
                    if (target !== null && mayOffer(p0.player, target)) place(p0.player, target);
                    else setHeld(null);
                    setOver(null);
                  }}
                  onPointerCancel={() => {
                    press.current = null;
                    setLift(null);
                    setOver(null);
                    setHeld(null);
                  }}
                  onPointerEnter={() => { if (legal && !lift) setOver(s.round); }}
                  onPointerLeave={() => { if (!lift) setOver((o) => (o === s.round ? null : o)); }}
                >
                  <span className="rd">
                    {s.live > 1 ? labelsFor(s.round).join(' / ') : labelFor(s.round)}
                  </span>
                  {s.keeper ? (
                    <button
                      type="button"
                      className={`keeperchip ${held === s.keeper ? 'held' : ''} ${pinned[s.keeper] ? 'pinned' : ''}`}
                      aria-pressed={held === s.keeper}
                      title={`${s.keeper} — drag to another pick, or tap to pick him up`}
                      disabled={readOnly || locking}
                      tabIndex={-1}
                      /*
                        THE TILE OWNS THE GESTURE NOW, so this stops competing for it — a button
                        inside the drag source swallows the pointerdown and the lift never starts,
                        which is the same class of bug that made the old drag need a click first.
                        It stays a button for the keyboard, and for the double-click that unpins.
                      */
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (readOnly || locking) return;
                        setHeld((h) => (h === s.keeper ? null : s.keeper));
                      }}
                      onDoubleClick={(e) => { e.stopPropagation(); clearPin(s.keeper!); }}
                    >
                      {/* `splitName`, not the last word: "Luther Burden III" is Burden III,
                          and the last word of it is "III", which named nobody. */}
                      {legal && over === s.round ? 'swap' : splitName(s.keeper).surname}
                    </button>
                  ) : (
                    <span className="what">
                      {legal && over === s.round
                        ? 'drop here'
                        : s.held === 0 ? 'traded' : s.live > 1 ? `${s.live} picks` : 'pick'}
                    </span>
                  )}
                </li>
              );
            })}
          </ol>
          {/*
            THE INSTRUCTIONS ARE GONE, THE MECHANISM IS NOT. Escalation is now a tap on a keeper's
            cost box, which opens a strip of the picks he can be charged to under his own row —
            so this strip is inventory, and a paragraph teaching a drag gesture is teaching the
            second way to do something that already has a first. The drag still works for anybody
            who finds it; it is no longer the documented path.
          */}
          {/*
            The four-colour key is gone with the four colours. What is left is the one thing a
            manager cannot work out by looking: how to move a keeper, and why the pick numbers
            are still dashes. HTML drag does not exist on a phone, so the tap is named first.
          */}
          {/*
            UNDER THE GRID, because that is where the eye is once the grid is open — it went
            looking for a pick, not back up to the card's title. And while a keeper is being
            carried this line stops being an instruction and becomes the state: who is in hand,
            and the two ways out of it.
          */}
          <p className={`striplegend${held ? ' carrying' : ''}`} aria-live="polite">
            {held ? (
              <>Moving <b>{splitName(held).surname}</b> — choose a highlighted pick, or press Esc.</>
            ) : locking ? (
              /* Nothing here can be moved, so nothing here offers to move it. */
              <>These picks are spent. Update the slate to move a keeper.</>
            ) : (
              <>
                Tap a keeper to pick him up, then tap an earlier pick — or drag him there.
                {!draft?.order?.length && <em> Pick numbers fill in once the draft order is set.</em>}
              </>
            )}
          </p>
          </>)}
        </section>

        </aside>

        <main className="kdmain">

        {blocked && (
          <section className="card banner bad">
            <div className="bannerhead">
              <span className="chip critical"><span className="ico">●</span>not a legal slate</span>
            </div>
            <ul className="tight">{assessment.errors.map((e) => <li key={e}>{e}</li>)}</ul>
            {/*
              TWO WAYS OUT, and neither of them is "work out which of your six pins caused it".
              The usual cause is hand-placed keepers holding early picks that a later keeper now
              needs; the assigner packs optimally when nothing is pinned, so releasing the pins is
              a real fix and not a guess. Starting again is the other one, and it is offered here
              rather than buried because a manager who has tied himself in a knot wants it.
            */}
            {!readOnly && (
              <div className="kdbannerfix">
                <button type="button" className="btn primary" disabled={!lowestCostFixes}
                  title={lowestCostFixes
                    ? 'Release every hand-placed pick and let the cheapest legal arrangement stand'
                    : 'Releasing the hand-placed picks would not make this slate legal — a keeper has to come off'}
                  onClick={releaseAllPins}>
                  Keep all at lowest cost
                </button>
                <button type="button" className="btn" onClick={resetSlate}>
                  Reset all keepers
                </button>
              </div>
            )}
          </section>
        )}


        {/*
          THE ROSTER, as eighteen rows rather than a table.
          One `DeclarationRow` at every width — the phone list and the desktop table were two
          markups of the same thing and drifted apart twice. Everything that used to live in a
          column of its own now hangs off the row: provenance moved into the chips and their
          callouts, the franchise column into the 78px tag slot, and the pick into a cost box that
          opens the escalation strip underneath.
        */}
        <section className="card kdlist">
          {/* Column headings, from 900px up. The phone has the sort control instead. */}
          <div className="kdcols" aria-hidden="true">
            <span>Keep</span><span>Player</span><span>Status</span>
            <span>Cost</span><span>Franchise</span>
          </div>

          {shown.map((r, i) => {
            const on = picked.has(r.player);
            /*
              WHERE THE SLATE ENDS — the SEAM, not every row past it.
              A submitted list sorts the eight you sent to the top, and without a line between
              them and the rest of the roster the two run together: the ninth row looks like a
              ninth keeper. This is true at exactly one row, the first unkept one, which means
              testing its predecessor rather than asking whether any keeper came earlier — the
              latter is true of every remaining row and drew thirteen dividers.

              Only while it is sent. Before that the list is one thing to choose from, and a
              divider halfway down it would be inventing a boundary.
            */
            const firstUnkept = locking && !on && i > 0 && picked.has(shown[i - 1]!.player);
            const isTagged = tagged === r.player;
            const gone = isGone(r.player);
            const landed = assessment.assigned.get(r.player);
            const natural = r.cost ?? 0;
            const round = landed ?? baseCost(r);
            const id = on ? identities.get(r.player) : undefined;
            const code = (id ? codeOfPick(id) : codeFor(round)) ?? `Rd ${round}`;
            const locked = stillForced(r) || readOnly || locking;

            return (
              <Fragment key={r.player}>
              {firstUnkept && (
                <p className="kdsplit">
                  <span>The rest of your roster</span>
                </p>
              )}
              <DeclarationRow
                key={r.player}
                player={r.player}
                pos={r.pos}
                chips={statusChips(r, {
                  farmForfeited: gone,
                  sleeperMinRound: LEAGUE_2026.sleeperMinRound,
                  lastSeason: SEASON - 1,
                })}
                on={on}
                locked={locked}
                disabled={readOnly}
                tag={on && !gone ? tagState(r, tagged, refuseTag(r)) : null}
                cost={on && !gone
                  ? { round, base: natural, code, overall: overallOfCode(code), tagged: isTagged }
                  : { round, base: natural, code: '', overall: null, tagged: false }}
                escalationOpen={escalating === r.player}
                onToggle={() => toggle(r)}
                onTag={() => toggleTag(r)}
                onCost={(x) => { setCaret(x); setEscalating((v) => (v === r.player ? null : r.player)); }}
              >
                <Escalation
                  open={escalating === r.player}
                  surname={splitName(r.player).surname}
                  options={escalationOptions(
                    natural,
                    myRounds,
                    round,
                    new Map([...occupiedRounds].filter(([, who]) => who !== r.player)),
                  )}
                  labelFor={(rd) => codeFor(rd) ?? `${rd}.\u2014\u2014`}
                  overallFor={(rd) => overallOfCode(codeFor(rd) ?? '')}
                  caret={caret}
                  onReset={pinned[r.player] !== undefined ? () => clearPin(r.player) : undefined}
                  onPick={(rd) => { pinTo(r.player, rd, round); setEscalating(null); }}
                />
                {/*
                  THE WITHIN-ROUND SWAP IS GONE. It offered "use your own 11th instead" where a
                  trade had left a GM holding two picks in one round, and it was asking a question
                  that does not exist yet: keepers are declared on 31 August and the lottery does
                  not run until committee night, so neither pick has a number and the two differ
                  by nothing a manager can act on. Tim, 31 August: "keep deadline comes before the
                  draft order is set. throw this line out."

                  The choice is not lost, only deferred — `resolvePickIdentities` still records a
                  `chargedPick` so keeper history reads "9.04" rather than "Round 9 (pick not
                  logged)", a saved `from` is still honoured on load, and moving a keeper between
                  the two cells is what the draft board is for once there are cells to move
                  between.
                */}
                {/*
                  THE COMMISSIONER RECORDS A FACT THE APP CANNOT KNOW: whether the NFL kept him.
                  The consequence is then the rules’, not his — the slot goes and the tax is
                  charged as though he had been kept, but the obligation to CARRY him ends, which
                  is what unlocks the checkbox above.
                */}
                {r.mandatory && canOverride && !readOnly && (
                  <p className="kdalt">
                    {gone ? (
                      <button type="button" className="linkbtn tiny"
                        onClick={() => void undoForfeit(r.player)}>
                        he made the roster after all
                      </button>
                    ) : (
                      <button type="button" className="linkbtn tiny"
                        onClick={() => { setForfeiting(r.player); setReleaseErr(null); }}>
                        never made a 53
                      </button>
                    )}
                  </p>
                )}
                {/*
                  NO "FRANCHISED IN AN EARLIER SEASON" LINK HERE. It was added on 27 August because
                  Tony Pollard could be tagged and should not have been, and the keeper file said
                  nothing — so the row offered to record the missing fact by hand.

                  Tim, 31 August: "Remove these links. They don't serve a purpose. You know who was
                  actually franchised before and factor that into the functionality." He is right,
                  and the diagnosis was wrong the first time: the app is NOT missing the concept.
                  Every keeper row already carries `taggedSeason` and `taggedSource`, four players
                  are already barred by it, and `tagEligible`/`tagReason` are computed from it
                  upstream. Pollard was a hole in the DATA, and the answer to a hole in the data is
                  to fill it — not to hang a correction affordance under all 177 rows, on every
                  manager's screen, forever.

                  The prior-tag override is still READ below, so anything already recorded through
                  the old control keeps barring the man it named. Nothing new can be written from
                  here. See `claude/franchise-tag-open-question-2026-08-27.md`.
                */}
                {forfeiting === r.player && (
                  <div className="farmrelease">
                    <p className="note tight">
                      <b>{r.player} is not on an NFL roster.</b> The keeper slot is gone and cannot
                      be filled, and the luxury tax is charged as though you had kept him{' '}
                      <Rule id="2019-II-5">§</Rule>. What ends is the obligation to carry him{' '}
                      <Rule id="2015-III-1">§</Rule> — his box unlocks, and his pick goes back on
                      the board.
                    </p>
                    <div className="segs tight">
                      {(Object.keys(FORFEIT_REASONS) as ForfeitReason[]).map((k) => (
                        <button key={k} type="button"
                          className={`seg ${forfeitKind === k ? 'on' : ''}`}
                          onClick={() => setForfeitKind(k)}>
                          {FORFEIT_REASONS[k].label}
                        </button>
                      ))}
                    </div>
                    <label className="notefield">
                      What happened
                      <input value={forfeitWhy}
                        placeholder="Waived by Seattle in final cuts, 26 August"
                        onChange={(e) => setForfeitWhy(e.target.value)} />
                    </label>
                    {releaseErr && <p className="note bad tight">{releaseErr}</p>}
                    <div className="actions">
                      <button type="button" className="linkbtn"
                        onClick={() => { setForfeiting(null); setForfeitWhy(''); }}>
                        Cancel
                      </button>
                      <button type="button" className="btn small primary"
                        onClick={() => void forfeitGraduate(r.player)}>
                        Record it — the slot is forfeited
                      </button>
                    </div>
                  </div>
                )}
              </DeclarationRow>
              </Fragment>
            );
          })}
        </section>

        {/*
          NO NOTE FIELD. It was an optional free-text box for the commissioner — a question, an
          unlogged trade — and it was the wrong door for both: a question wants an answer and this
          form does not produce one, and an unlogged trade belongs in the trade log where it
          reprices picks. Removed rather than relabelled; the slate says what it says.
        */}

        {/*
          THE REST OF THE LEAGUE, rebuilt and no longer a commissioner's fold.
          What sat here was the commissioner's review queue, collapsed and gated. It is now the
          league's board: the full priced reference list with every submitted declaration laid
          over it, open to everybody, absorbing what used to be the separate `Keepers ›
          Draftboard` tab. It sits under your own slate because the question it answers — "who
          else has declared him?" — is the one you ask while filling yours in.
        */}
        <section className="card">
          {/*
            The charge band is handed the two facts this screen has and that one does not: who is
            pressing it, and whether declarations have shut. Withheld entirely below commissioner
            and while keepers are open, so the band is never drawn where it could not be used.
          */}
          <EverySlate store={store} you={gm}
            {...(canOverride ? { charge: { actor, locked: !gate.open } } : {})} />
        </section>

        </main>
       </div>
      </Linkify>

      {/*
        The bar, and the sheet it opens. Outside `Linkify` because it is chrome rather than prose
        and has no rule references to mark up.
      */}
      {!readOnly && (
        <CommitBar
          saved={saveState}
          disabled={saving || blocked || charged.length === 0}
          submitted={status === 'submitted' && !editing}
          count={charged.length}
          tax={faab(assessment.tax.taxFaab)}
          picks={assessment.assigned.size}
          onSubmit={() => setConfirming(true)}
          onUpdate={() => setEditing(true)}
          onWithdraw={() => setClearing(true)}
        />
      )}

      {/*
        WITHDRAWING IS A DIALOG, not a card at the bottom of the page.
        It used to render as a `<section>` in the main column — which on a desktop is below
        sixteen roster rows and the whole Every-slate board, with the button that opened it up in
        the rail. Pressing Withdraw appeared to do nothing, because what it did was two thousand
        pixels away. It is destructive and has no undo, so the ask stays; it just has to be where
        the asking happened.

        AND IT STILL WAS NOT (Tim, 26 August): "the withdraw slate confirmation was not visible
        when I tapped it. I had to scroll down to find it and it was covered by the buttons."
        Making it `position: fixed` was not enough, because pull-to-refresh transforms `main` and
        a transform captures fixed descendants — so it was fixed to the bottom of a 2,837px
        column. `dock` is the fix; see `src/ui/dock.tsx`.
      */}
      {clearing && dock(
        <>
          <button type="button" className="sheetscrim kdscrim" aria-label="Keep the slate"
            onClick={() => setClearing(false)} />
          <div className="kdsheet" role="dialog" aria-modal="true" aria-label="Withdraw your slate">
            <h2 className="kdsheeth">Withdraw your slate?</h2>
            {/*
              "nothing is kept" was also untrue — `clearSlate` puts the mandatory farm graduates
              back, because 2013-III-1 §5 does not let a GM drop them.
            */}
            <p className="kdsheetsub">
              The league stops seeing it. You can lock again before {dueWhen ?? 'the deadline'}.
            </p>
            <div className="kdsheetacts">
              <button type="button" className="kdsubmit danger" disabled={saving}
                onClick={() => { setClearing(false); setEditing(false); void clearSlate(); }}>
                {saving ? 'Withdrawing\u2026' : 'Withdraw it'}
              </button>
              <button type="button" className="kdkeep" onClick={() => setClearing(false)}>
                Keep it
              </button>
            </div>
          </div>
        </>,
      )}

      {/*
        THE UNLOCK EXCEPTION, AND THE SECOND ACT THAT MAKES IT ONE.
        Same dock and same sheet as Withdraw, because it is the same class of thing: an act with a
        consequence somebody else has to live with. The copy states all three consequences rather
        than asking "are you sure" — the announced deadline does not move, the tax that has already
        been charged against this slate stops matching it, and the log gets a line naming him.
      */}
      {unlocking && dock(
        <>
          <button type="button" className="sheetscrim kdscrim" aria-label="Leave it locked"
            onClick={() => setUnlocking(false)} />
          <div className="kdsheet" role="dialog" aria-modal="true"
            aria-label="Unlock this slate as an exception">
            <h2 className="kdsheeth">
              Unlock {gm.toUpperCase() === actor.toUpperCase() ? 'your own' : `${managerShort(gm)}\u2019s`} slate?
            </h2>
            <p className="kdsheetsub">
              Declarations closed {dueWhen ?? 'at the deadline'} and stay closed for everybody else.
              Any luxury tax already charged will need charging again from League Overview. This is
              written to the activity log against your name.
            </p>
            <div className="kdsheetacts">
              <button type="button" className="kdsubmit danger"
                onClick={() => {
                  setUnlocking(false);
                  setException(true);
                  note(store, actor, {
                    kind: 'keeper',
                    what: `Unlocked ${managerShort(gm)}\u2019s slate after the deadline`,
                    target: `declarations/${gm.toUpperCase()}`,
                    ...(gm.toUpperCase() !== actor.toUpperCase() ? { onBehalfOf: gm } : {}),
                    detail: {
                      'Deadline': dueWhen ?? 'not set',
                      'Reason': 'commissioner unlock exception',
                    },
                  });
                }}>
                Unlock it
              </button>
              <button type="button" className="kdkeep" onClick={() => setUnlocking(false)}>
                Leave it locked
              </button>
            </div>
          </div>
        </>,
      )}

      {confirming && (
        <SubmitSheet
          lines={submitLines}
          tax={faab(assessment.tax.taxFaab)}
          taxed={assessment.tax.taxFaab > 0}
          faabAfter={faab(faabAfterTax)}
          until={dueWhen}
          busy={saving}
          onConfirm={() => { setConfirming(false); setEditing(false); void save('submitted'); }}
          onCancel={() => setConfirming(false)}
        />
      )}
    </div>
  );
}
